<script setup>
import axios from '@/helpers/axios'
import Loader from '@/views/Loader.vue'
import Footer from '@/views/front-pages/front-page-footer.vue'
import Navbar from '@/views/front-pages/front-page-navbar.vue'
import { LControlLayers, LMap, LMarker, LPopup, LTileLayer } from "@vue-leaflet/vue-leaflet"
import { useTitle } from '@vueuse/core'
import L from 'leaflet'
import "leaflet/dist/leaflet.css"
import { useI18n } from 'vue-i18n'
import AppSearchHeaderBg from '@images/pages/app-search-header-bg.png'
import { useTheme } from 'vuetify'

definePage({
  meta: {
    layout: 'blank',
    public: true
  }
})

const vuetifyTheme = useTheme()


const currentTheme = vuetifyTheme.current.value.colors


const router = useRouter()
const { t } = useI18n()

useTitle(t('landing.stations') + ' - Kropie')

const devices = ref(null)
const locations = ref([])

const accessToken = ref('pk.eyJ1IjoicmVuYXRvbXMiLCJhIjoiY2pzcDNpaDdlMHFvcTQ5cWx3eXJlMDZ2eiJ9.3GsYRV5ahGFqCpb01u7qFw')
const urlOutdoors = ref('https://api.mapbox.com/styles/v1/mapbox/outdoors-v11/tiles/{z}/{x}/{y}?access_token=' + accessToken.value)
const urlSattelite = ref('https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=' + accessToken.value)

const mapRef = ref(null)
const zoom = ref(12)
const center = ref([0, 0])


// -----------------------------
// Map setup
// -----------------------------
function onMapReady(map) {
  mapRef.value = map
}

// -----------------------------
// Dynamic bounds logic
// -----------------------------
async function fitToDeviceBounds() {
  if (!mapRef.value || !devices.value?.length) return

  // Filter devices with valid coordinates
  const coords = devices.value
    .filter(d => d.last_location && d.last_location.latitude && d.last_location.longitude)
    .map(d => [d.last_location.latitude, d.last_location.longitude])

  if (!coords.length) return

  // Compute bounds
  const bounds = L.latLngBounds(coords)

  // Wait for DOM and map render
  await nextTick()
  mapRef.value.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 })
}

const updateData = async () => {
  axios.get('/landing/stations').then(response => {
    devices.value = response.data.data
    fitToDeviceBounds()
  }).catch(e => {
    console.error(e)
  })
}

updateData()


watch(
  () => devices.value,
  () => fitToDeviceBounds(),
  { deep: true, immediate: true }
)


// -----------------------------
// Marker avatar
// -----------------------------
function getHtmlAvatar(item) {
  const statusColors = {
    online: currentTheme.success,
    delayed: currentTheme.warning,
    hibernated: currentTheme.error,
    default: currentTheme.secondary,
  }

  const statusIcons = {
    online: 'tabler-wifi',
    delayed: 'tabler-clock-hour-4',
    hibernated: 'tabler-player-pause-filled',
    default: 'tabler-settings',
  }

  const color = statusColors[item.status] || statusColors.default
  const icon = statusIcons[item.status] || statusIcons.default

  return `
    <div
      style="
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background-color: ${color};
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid white;
        box-shadow: 0 0 4px rgba(0,0,0,0.2);
      "
    >
      <i
        class="${icon}"
        style="color: white; font-size: 14px;"
      ></i>
    </div>
  `
}

function getCustomIcon(item) {
  return L.divIcon({
    className: 'leaflet-avatar-icon',
    html: getHtmlAvatar(item),
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  })
}

const onMouseOver = e => e.target.openPopup()
const onMouseOut = e => e.target.closePopup()

function onMarkerClick(item) {
  // 🧭 Option A: Navigate directly
  router.push({ name: 'stations-uuid', params: { uuid: item.uuid } })

  // 🧠 Option B: If you prefer emitting to parent instead
  // emit('marker-click', { deviceUuid: item.uuid, itemUuid: props.data.uuid })
}


</script>

<template>
  <div class="help-center-page">
    <Navbar />
    <div class="content-wrapper">
        <VCard
            flat
            class="text-center search-header"
            :style="`background: url(${AppSearchHeaderBg});`"
        >
            <VCardText>
              <h3 class="text-h2 font-weight-medium py-10">
                {{ $t('landing.stations') }}
              </h3>

              <p class="text-disabled mb-6">
                {{ $t('landing.stations_description') }}
              </p>
            </VCardText>
        </VCard>


        <div class="py-16 bg-surface">
            <VContainer>
              <VCard
                class="pa-0 overflow-visible cursor-pointer"
                elevation="2"
                outlined
                style="border-radius: 16px;"
              >
                <VCardText class="pa-0">
                  <div style="height: 100%; width: 100%; border-radius: 15px; overflow: hidden;">
                    <LMap
                      :zoom="zoom"
                      :center="center"
                      :min-zoom="0"
                      @ready="onMapReady"
                      style="height: 500px; width: 100%;"
                    >
                      <LFullScreen :map-ref="mapRef" />
                      <LTileLayer
                        :url="urlSattelite"
                        layer-type="base"
                        name="Mapbox Satellite"
                        visible
                      />
                      <div v-for="(item, index) in devices" :key="index" >
                        <LMarker
                          v-if="item.last_location"
                          :lat-lng="[item.last_location.latitude, item.last_location.longitude]"
                          :icon="getCustomIcon(item)"
                          @mouseover="onMouseOver($event)"
                          @mouseout="onMouseOut($event)"
                          @click="onMarkerClick(item)"
                        >
                        <LPopup :options="{ autoClose: false, closeOnClick: false }">
                            {{ item.name }} <!-- Or any other data -->
                          </LPopup>
                        </LMarker>
                      </div>
                    </LMap>
                  </div>
                </VCardText>
              </VCard>
            </VContainer>
        </div>
        <div class="page-pricing-free-trial-banner-bg">
          <VContainer>
            <div class="d-flex align-center flex-md-row flex-column position-relative">
              <div class="text-center text-md-start py-4 px-10 px-sm-0">
                <h3 class="text-h3 text-primary mb-2">
                  {{ $t('landing.own_station') }}
                </h3>
                <p>
                  {{ $t('landing.own_station_description') }}
                </p>
                <VBtn
                  class="mt-4"
                  to="/#contact-us"
                >
                  {{ $t('landing.contact_us') }}
                </VBtn>
              </div>
              <div class="free-trial-illustrator">
                <VImg
                  :src="laptopGirl"
                  :width="202"
                />
              </div>
            </div>
          </VContainer>
        </div>
    </div>

    <Footer />
  </div>
</template>


<style lang="scss">
.help-center-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  .content-wrapper {
    flex: 1; // This makes the content expand to fill the available space
  }

  .search-header {
    background-size: cover !important;
    padding-block-start: 7rem !important;
  }
}

@media (max-width: 960px) and (min-width: 600px) {
  .help-center-page {
    .v-container {
      padding-inline: 2rem !important;
    }

    .search-header {
      padding: 5rem !important;
    }
  }
}

@media (max-width: 599px) {
  .help-center-page {
    .search-header {
      padding-block-end: 2rem !important;
      padding-block-start: 4rem !important;
      padding-inline: 2rem !important;
    }
  }
}
</style>
